import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as dateUtils from '@owl-nest/date'
import { t } from '@owl-nest/localize'

import * as model from '@owl-nest/models'
import {
  BREAKPOINTS,
  COLORS,
  glyphs,
  illustrations,
  Pastille,
  styles as S,
  ZINDEX,
} from '@ulule/owl-kit-components/next'

import { Project } from '../../../../models/project'
import { RootState } from '../../../../../reducers'
import { useNavigation } from '../../../../hooks/useNavigation'
import { PATHS } from '../../../../paths'
import * as projectModel from '../../../../models/project'

import * as styles from '../../style'

type NewsPushProps = {
  news: api.News
}

export function NewsPush({ news }: NewsPushProps): React.ReactElement<NewsPushProps> {
  const navigation = useNavigation()
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const formattedDate =
    news.date_publication &&
    dateUtils.formatDistanceToNow(new Date(news.date_publication ?? news.date_creation), { addSuffix: true })
  const isUserAllowed = Boolean(news.permissions.self?.includes('read'))
  const isNewsMasked = !isUserAllowed

  return (
    <Push
      onClick={() => {
        if (model.project.isFinished<projectModel.ExtraFields>(project) || !isNewsMasked) navigation.to(PATHS.NEWS)
      }}
    >
      {!isNewsMasked ? (
        <PushWrapper>
          <PushContent>
            <Pastille noBorder size="extraSmall" style={{ backgroundColor: COLORS.GREY_SHADE_5 }}>
              <illustrations.twoToned.PaperPlane size={40} />
            </Pastille>
            <Info>
              <S.heading.XXXXXS>{formattedDate}</S.heading.XXXXXS>
              <S.heading.XS>{model.i18n.get(news.title)}</S.heading.XS>
            </Info>
          </PushContent>
          <ReadMore>{t('Read more')}</ReadMore>
        </PushWrapper>
      ) : (
        <>
          <Blurred>
            <Pastille noBorder size="extraSmall" style={{ backgroundColor: COLORS.GREY_SHADE_5 }}>
              <illustrations.twoToned.PaperPlane size={40} />
            </Pastille>
            <ReadMore>{t('Read more')}</ReadMore>
          </Blurred>

          <BlurredTop>
            <BlurredInfo>
              <glyphs.stroke.Unread size={32} />
              {(news.audience_type == api.NewsAudienceType.TIP ||
                news.audience_type == api.NewsAudienceType.SUPPORTERS) && (
                <S.copy.M.light>{t('This post is for backers only')}</S.copy.M.light>
              )}
              {news.audience_type == api.NewsAudienceType.FANS && (
                <S.copy.M.light>{t('This post is for fans only')}</S.copy.M.light>
              )}
            </BlurredInfo>
            {!model.project.isFinished<projectModel.ExtraFields>(project) && (
              <S.button.Button
                onClick={() => {
                  if (project.is_online) {
                    navigation.to(PATHS.REWARDS)
                  }
                }}
                size="small"
              >
                {t('Back this project')}
              </S.button.Button>
            )}
          </BlurredTop>
        </>
      )}
    </Push>
  )
}

const Push = styled.div`
  align-items: flex-end;
  border: 1px solid ${COLORS.GREY_SHADE_5};
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  margin-bottom: 53px;
  padding: 20px 20px 20px 11px;
  position: relative;
  min-height: 120px;
  transition: 0.3s ease;

  ${Pastille} {
    margin-right: 15px;
  }

  &:hover {
    box-shadow: 2px 4px 6px 1px ${COLORS.hexToRgb(COLORS.GREY_SHADE_4, 0.5)};
  }

  @media screen and ${BREAKPOINTS.TABLET} {
    min-height: 90px;
  }
`

const PushContent = styled.div`
  display: flex;
`

const ReadMore = styled(S.heading.XXXXXS)`
  white-space: nowrap;
  padding-left: 15px;
  text-align: right;
`

const Info = styled.div`
  flex: 5;
`

const Blurred = styled(styles.Blurred)`
  align-items: flex-end;
  justify-content: inherit;
  width: 100%;
  z-index: ${ZINDEX.ABOVE};

  &::before {
    box-shadow:
      40px 0 0 ${COLORS.GREY_SHADE_5},
      80px 0 0 ${COLORS.GREY_SHADE_5},
      120px 0 0 ${COLORS.GREY_SHADE_5},
      160px 0 0 ${COLORS.GREY_SHADE_5},
      0px 40px 0 ${COLORS.GREY_SHADE_5},
      40px 40px 0 ${COLORS.GREY_SHADE_5},
      80px 40px 0 ${COLORS.GREY_SHADE_5},
      120px 40px 0 ${COLORS.GREY_SHADE_5},
      160px 40px 0 ${COLORS.GREY_SHADE_5},
      200px 40px 0 ${COLORS.GREY_SHADE_5},
      240px 40px 0 ${COLORS.GREY_SHADE_5},
      260px 40px 0 ${COLORS.GREY_SHADE_5},
      300px 40px 0 ${COLORS.GREY_SHADE_5};
    left: 80px;
    top: -9px;
  }

  ${ReadMore} {
    flex: 1;
    text-align: right;
  }
`

const BlurredTop = styled.div`
  align-items: center;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 0;
  min-height: 70px;
  padding: 0 10px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: ${ZINDEX.ABOVE};

  @media screen and ${BREAKPOINTS.TABLET} {
    flex-direction: row;
    ${S.button.Button} {
      margin-left: 38px;
    }
  }

  ${glyphs.stroke.Unread} {
    color: ${COLORS.GREY_SHADE_7};
    margin-right: 10px;
  }
`

const BlurredInfo = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  padding-left: 20px;

  @media screen and ${BREAKPOINTS.TABLET} {
    padding: 0;
  }
`

const PushWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;

  @media screen and ${BREAKPOINTS.TABLET} {
    flex-direction: row;
    align-items: flex-end;
  }
`
